// ============================================================================
// Parametrização multi-junta por REGISTRY = (UF, município).
// Decisão: [[decisoes/2026-07-07-multi-junta-registry-uf-municipio]].
//
// O backbone de etapas é NACIONAL (REDESIM/DREI). O que varia por registry:
//  - os sub-passos da etapa 2 (Viabilidade/Licenciamento) — AR-01;
//  - os rótulos de protocolo por PAPEL (o coletor/DBE é federal universal) — AR-02;
//  - a guia de registro (GO/SP=DARE; RJ=emolumentos próprios, distrato isento) — AR-03;
//  - prazos (RJ=30 dias p/ abrir a FIT, senão cancela por inatividade);
//  - órgãos pós-registro e extras (Proteção de CPF, Facilita SP).
//
// GO validado (guia do contador). SP/RJ VALIDADOS com o operador + fonte primária
// (2026-07-07): a ESTRUTURA está confirmada. Ressalvas de conteúdo fino — refinar só
// ao automatizar os RPAs (o foco do kanban é rastreabilidade geral):
//  - RJ: a CPL deferida é PRÉ-REQUISITO do Pedido de Viabilidade (não ✓ paralelo).
//  - SP-interior: há passos pós-registro (Inscrição Municipal/CCM → CLI); a viab. é o 1º.
//  - Facilita SP (registro automático) é POR MUNICÍPIO (~383→430 dos 645), não o interior todo.
// VALORES (emolumentos RJ) NÃO hardcodar — Deliberação JUCERJA nº 171/2025 (Anexos I/II).
// ============================================================================

import type { TipoLegalizacao } from '../../../hooks/useLegalizacao';
import type { TipoAlteracao } from '../../../hooks/useSocietario';

/**
 * Sigla da junta comercial por UF. Existe UMA junta por estado — a relação é 1:1 e
 * fixa, então a sigla é DERIVADA da UF, nunca digitada ao lado dela.
 *
 * ⚠️ Motivo de existir: `junta_sigla` e `junta_uf` eram dois campos de texto livre
 * independentes no modal de criação, com defaults JUCEG e GO. Bastava trocar a UF e
 * esquecer a sigla — e foi o que aconteceu: o banco de dev tem um processo gravado
 * como `JUCEG · MG`, combinação que não existe. Como o filtro por junta e a resolução
 * de registry chaveiam por UF, dado assim produz card que cai num recorte e exibe
 * outro rótulo.
 *
 * GO, RJ e SP conferem com o `nome` dos registries abaixo (validados com o operador).
 * As demais vêm da nomenclatura pública das juntas e valem uma conferida antes de
 * entrarem em documento oficial — aqui servem de rótulo e de trava contra combinação
 * impossível.
 */
export const JUNTA_POR_UF: Record<string, string> = {
  AC: 'JUCEAC',
  AL: 'JUCEAL',
  AM: 'JUCEA',
  AP: 'JUCAP',
  BA: 'JUCEB',
  CE: 'JUCEC',
  // JUCIS-DF, não "JCDF" (nome antigo). Confirmado 2026-08-17 no site da própria junta
  // (jucis.df.gov.br) e no diretório de juntas do DREI/gov.br.
  DF: 'JUCIS-DF',
  ES: 'JUCEES',
  GO: 'JUCEG',
  MA: 'JUCEMA',
  MG: 'JUCEMG',
  MS: 'JUCEMS',
  MT: 'JUCEMAT',
  PA: 'JUCEPA',
  PB: 'JUCEP',
  PE: 'JUCEPE',
  PI: 'JUCEPI',
  PR: 'JUCEPAR',
  RJ: 'JUCERJA',
  RN: 'JUCERN',
  RO: 'JUCER',
  RR: 'JUCERR',
  RS: 'JUCISRS',
  SC: 'JUCESC',
  SE: 'JUCESE',
  SP: 'JUCESP',
  TO: 'JUCETINS',
};

/**
 * UFs onde a Especialistas tem cliente — a lista do seletor de criação.
 *
 * Curada de propósito, não `Object.keys(JUNTA_POR_UF)`: oferecer as 27 é rolagem longa
 * e 13 opções que nunca vão ser escolhidas, num campo que se preenche em toda criação.
 * Levantada da base de clientes em 2026-08-12.
 *
 * ⚠️ O mapa `JUNTA_POR_UF` acima continua COMPLETO de propósito. Ele resolve o rótulo de
 * qualquer processo já gravado — inclusive de UF fora desta lista. Estreitar o mapa
 * junto faria processo antigo perder a sigla na tela.
 *
 * Cliente novo em estado fora daqui? É acrescentar a UF nesta linha.
 */
export const UFS_ATENDIDAS = [
  'AL',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MG',
  'MS',
  'PB',
  'PE',
  'PR',
  'RJ',
  'SC',
  'SP',
];

export type MunicipioScope = 'capital' | 'interior' | 'todos';

export interface RegistryChecklistItem {
  key: string;
  label: string;
  req: boolean;
}

/** Um passo da etapa 2 composta (Viabilidade/Licenciamento). */
export interface ViabilidadeSubpasso {
  key: string;
  label: string;
  /** onde o passo é feito (portal/órgão) — só rótulo pro operador. */
  orgao: string;
  checklist: RegistryChecklistItem[];
  /**
   * Escopo por TIPO de legalização. Se presente, o subpasso só se aplica a esses
   * tipos (ex.: o Balcão Único de SP é conceito de ABERTURA → só `constituicao`).
   * Ausente = aplica a todos os tipos.
   */
  tipos?: TipoLegalizacao[];
  /**
   * Escopo por GATILHO da alteração (só relevante quando `tipo=alteracao`). Se
   * presente, o subpasso só entra quando os `tipos_alteracao` do processo tocam
   * um desses eventos (ex.: a CPL do RJ só quando muda endereço/atividade). A
   * DIREÇÃO DE FALHA é fail-open: tipos indeterminados → NÃO filtra por gatilho
   * (mostra o subpasso). Ver `subpassosAplicaveis` em `legalizacaoPlaybook.ts`.
   */
  requerAlteracaoDe?: TipoAlteracao[];
}

export interface RegistryConfig {
  id: string;
  uf: string;
  municipioScope: MunicipioScope;
  /**
   * Registry montado pelo PADRÃO NACIONAL, não levantado nesta UF.
   *
   * Existe pra tela poder dizer isso ao operador. O denominador comum foi verificado em
   * 8 estados (pesquisa 2026-08-17): viabilidade em ato único, análise de endereço sempre
   * municipal, exigência de 30 dias corridos da IN DREI 81/2020, DBE federal.
   *
   * ⚠️ E a MESMA pesquisa mostrou por que ele precisa se anunciar: em 2 dos 3 estados
   * levantados o padrão NÃO vale — no DF a viabilidade de localização é ato eliminatório
   * anterior, e em Salvador o TVL municipal é pré-requisito. Nos dois casos a diferença é
   * um passo OBRIGATÓRIO que o genérico não mostra. Genérico calado mentiria com
   * confiança em algum estado que ninguém levantou.
   */
  generico?: boolean;
  /** rótulo pro operador (ex.: "JUCEG", "JUCESP · Balcão Único"). */
  nome: string;
  portal: string;
  viabilidadeSubpassos: ViabilidadeSubpasso[];
  /**
   * protocolos por papel (coletor = DBE federal, universal). `abertura`: o da viabilidade quando é constituição e a
   * junta usa outro prefixo (GO: GOB na abertura, GOP na alteração); ausente = o de `viabilidade`.
   */
  protocoloLabels: { viabilidade: string; coletor: string; registro: string; abertura?: string };
  /** guia genérica — nunca hardcodar valor; puxar da tabela oficial. */
  guia: { nome: string; tabelaUrl?: string; distratoIsento?: boolean };
  /** prazo de inatividade que cancela o protocolo (RJ = 30 dias / FIT). */
  prazoInatividadeDias?: number;
  /** nota/tooltip do prazo (o que vence e por quê). */
  prazoNota?: string;
  /**
   * SLA típico da análise de VIABILIDADE, em dias (default de prazo p/ o operador,
   * mesmo sem RPA). Confirmado 2026-07-09: SP ≈ 2 dias úteis; RJ manifestação do
   * município ≈ 48h. GO não publicado (deixar ausente).
   */
  slaViabilidadeDias?: number;
  /**
   * Órgãos rastreados na etapa "Demais órgãos" (itens `req: false` — rastreio,
   * não travam o Avançar).
   *
   * Sessão 2026-08-06 (Juliana, analista paralegal): enxugado para **órgão do
   * estado + Prefeitura**. Saíram Bombeiros/CBMGO, Vigilância Sanitária e
   * Ambiental — são exigências por atividade (CNAE), não etapas de toda
   * legalização, e poluíam o checklist na maioria dos processos.
   *
   * `url` (mesma sessão, pedido dela: "colocar links de acesso aos órgãos
   * visíveis em cada card para facilitar o colaborador de acessar") vira link
   * no item do checklist. URLs conferidas no catálogo do brain
   * (`analises/2026-07-10-catalogo-links-orgaos-legalizacao`, validado-web).
   * Opcional: sem URL o item renderiza como antes. Prefeitura NUNCA tem URL
   * (time, 16/09): o sistema varia por município e o registry cobre a UF
   * inteira — empresa de Anápolis abria o SICAE de Goiânia. Só links
   * estaduais e federais.
   *
   * `tipos`: item que só aparece nesses tipos de legalização (ausente = todos).
   *
   * `rotulo`: texto do item na tela, quando precisa dizer mais que o nome. A key continua saindo do `nome`
   * (é o que está gravado nos cards): trocar o texto é aqui, nunca no `nome`.
   */
  orgaosPosRegistro: { nome: string; rotulo?: string; url?: string; tipos?: TipoLegalizacao[] }[];
  /**
   * Abertura pelo Balcão Único, sem DBE (19/10): a junta manda os dados à Receita, e o card não pede DBE nem o
   * Coletor na constituição. Alteração e baixa seguem com DBE. Só onde o manual da junta confirma.
   */
  aberturaSemDbe?: boolean;
  /**
   * Portal integrador da UF (JUCEG / VRE|REDESIM / REGIN) — onde a maior parte
   * das etapas acontece. Exibido no topo do drawer com o nome que já vinha em
   * `portal`, para o operador não ter que procurar o link em cada etapa.
   */
  portalUrl?: string;
  extras?: { protecaoCpf?: boolean; facilitaSp?: boolean };
}

export const REGISTRIES: RegistryConfig[] = [
  // ── GO · JUCEG (VALIDADO) ──────────────────────────────────────────────────
  {
    id: 'GO',
    uf: 'GO',
    municipioScope: 'todos',
    nome: 'JUCEG',
    portal: 'Portal do Empreendedor Goiano',
    // 19/10: a viabilidade virou dois passos para a alteração pedir o nome só quando ele muda (Camila: "Nome
    // aprovado apenas na alteração da razão social"; natureza jurídica também muda o nome). As keys dos itens são
    // as de antes: o que está marcado nos cards continua.
    viabilidadeSubpassos: [
      {
        key: 'viab_nome',
        label: 'Nome empresarial',
        orgao: 'JUCEG',
        requerAlteracaoDe: ['razao_social', 'natureza_juridica'],
        checklist: [{ key: 'nome_aprovado', label: 'Nome aprovado (junta comercial)', req: true }],
      },
      {
        key: 'viab_endereco',
        label: 'Endereço e atividade',
        orgao: 'Prefeitura',
        checklist: [
          { key: 'endereco_aprovado', label: 'Endereço aprovado (prefeitura)', req: true },
        ],
      },
    ],
    // GOB na abertura (Balcão Único), GOP na alteração com consulta prévia, GON sem: manuais JUCEG 2026.
    protocoloLabels: { viabilidade: 'GOP', coletor: 'DBE', registro: 'GOP/GON', abertura: 'GOB' },
    guia: { nome: 'DARE' },
    portalUrl: 'https://www.portaldoempreendedorgoiano.go.gov.br/',
    // Manual de abertura LTDA/EI 2026 da JUCEG: "Não é necessário preencher o DBE".
    aberturaSemDbe: true,
    orgaosPosRegistro: [
      // A IE sai pela Redesim, mas pode vir precária (DTE, dados, contador): IN 946/09-GSE, art. 19-A.
      {
        nome: 'SEFAZ-GO',
        rotulo: 'SEFAZ-GO: IE ativa (sem precariedade)',
        url: 'https://goias.gov.br/economia/cce/',
      },
      { nome: 'Prefeitura' },
    ],
  },

  // ── SP-capital · JUCESP Balcão Único (RASCUNHO — validar) ──────────────────
  {
    id: 'SP-capital',
    uf: 'SP',
    municipioScope: 'capital',
    nome: 'JUCESP · Balcão Único',
    portal: 'VRE|REDESIM',
    slaViabilidadeDias: 2, // vreredesim FAQ (2 dias úteis)
    viabilidadeSubpassos: [
      {
        key: 'balcao_unico',
        label: 'Balcão Único (entrada única)',
        orgao: 'JUCESP',
        // Balcão Único / Entrada Única de Dados = modelo de ABERTURA de baixo risco
        // da cidade de SP, disponível só p/ o município de SP. NÃO se aplica a
        // alteração (que passa pelo módulo de viabilidade). V-SP-01 (2026-07-09).
        tipos: ['constituicao'],
        checklist: [{ key: 'balcao_ok', label: 'Registro integrado deferido', req: true }],
      },
      {
        // Alteração na capital usa o módulo de viabilidade do VRE|REDESIM, não o
        // Balcão Único. Mesma forma do interior no nível da viabilidade. V-SP-01.
        key: 'viab_alt',
        label: 'Viabilidade',
        orgao: 'JUCESP (nome) + Prefeitura (endereço)',
        tipos: ['alteracao'],
        checklist: [{ key: 'viab_ok', label: 'Viabilidade aprovada', req: true }],
      },
    ],
    protocoloLabels: {
      viabilidade: 'Protocolo de Viabilidade',
      coletor: 'DBE',
      registro: 'Protocolo REDESIM',
    },
    guia: { nome: 'DARE' },
    portalUrl: 'https://www.vreredesim.sp.gov.br/',
    orgaosPosRegistro: [
      { nome: 'SEFAZ-SP', url: 'https://www.cadesp.fazenda.sp.gov.br/' },
      { nome: 'Prefeitura (CCM)' },
    ],
    // facilitaSp: registro AUTOMÁTICO exige contrato-padrão do sistema → é feature de
    // CONSTITUIÇÃO de baixo risco; inócuo em alteração (doc próprio = fluxo tradicional).
    // Valida o trade-off do Caminho B (byte-fidelity ⇒ sem deferimento automático). V-CAMB-01.
    extras: { protecaoCpf: true, facilitaSp: true },
  },

  // ── SP-interior · fluxo tradicional (RASCUNHO — depende de PQ-01) ──────────
  {
    id: 'SP-interior',
    uf: 'SP',
    municipioScope: 'interior',
    nome: 'JUCESP · fluxo tradicional',
    portal: 'VRE|REDESIM',
    slaViabilidadeDias: 2, // vreredesim FAQ (2 dias úteis)
    viabilidadeSubpassos: [
      {
        key: 'viab',
        label: 'Viabilidade',
        orgao: 'JUCESP (nome) + Prefeitura (endereço)',
        checklist: [{ key: 'viab_ok', label: 'Viabilidade aprovada', req: true }],
      },
      // O LICENCIAMENTO INTEGRADO SAIU DAQUI em 2026-08-27 (pedido do operador):
      // ele nao se encerra na 2a etapa. A viabilidade aprova nome + endereco; o
      // licenciamento da prefeitura conclui DEPOIS, junto das demais inscricoes.
      // Como subpasso `req: true` ele travava o "Avancar" da etapa por algo que
      // ainda nem tinha como estar pronto. Virou marcador de rastreio em
      // `orgaosPosRegistro` ("Demais orgaos"), onde os itens sao `req: false`; e em
      // 19/10 foi para a coluna "Licenciamento" da constituicao.
      // Cards antigos guardam `lic_ok` no bloco da viabilidade: dado orfao, que
      // ninguem mais le - a marcacao nova mora na chave `org_*` da outra etapa.
    ],
    protocoloLabels: {
      viabilidade: 'Protocolo de Viabilidade',
      coletor: 'DBE',
      registro: 'Protocolo REDESIM',
    },
    guia: { nome: 'DARE' },
    portalUrl: 'https://www.vreredesim.sp.gov.br/',
    orgaosPosRegistro: [
      { nome: 'SEFAZ-SP', url: 'https://www.cadesp.fazenda.sp.gov.br/' },
      // Interior: o sistema municipal varia — sem URL, pra nao mandar o
      // operador pro municipio errado.
      { nome: 'Prefeitura' },
      // 'Licenciamento integrado (Prefeitura)' (so' constituicao) saiu em 19/10: a constituicao
      // ganhou a coluna "Licenciamento", como a alteracao em 2026-09-11. A key `org_licenciamento_
      // integrado_prefeitura` dos cards antigos fica orfa (inofensivo).
    ],
    extras: { protecaoCpf: true },
  },

  // ── RJ-capital · JUCERJA/REGIN, CPL antes (RASCUNHO — validar) ─────────────
  {
    id: 'RJ-capital',
    uf: 'RJ',
    municipioScope: 'capital',
    nome: 'JUCERJA · Rio',
    portal: 'REGIN-RJ / Protocolo Web',
    slaViabilidadeDias: 2, // manifestação do município ≈ 48h (JUCERJA ReginFolder)
    viabilidadeSubpassos: [
      {
        key: 'cpl',
        label: 'CPL — Consulta Prévia de Localização',
        orgao: 'Carioca Digital (Prefeitura do Rio)',
        // A CPL é pré-requisito SÓ quando muda endereço e/ou atividade (CNAE). Uma
        // alteração só de nome NÃO exige CPL — só a Viabilidade REGIN. V-RJ-01
        // (2026-07-09). Em constituição a CPL sempre se aplica (sem `requerAlteracaoDe`
        // não filtra fora de alteração). Status literal do REGIN ainda HIP (confirmar por print).
        requerAlteracaoDe: ['endereco', 'objeto_social'],
        checklist: [
          {
            key: 'cpl_deferida',
            label: 'CPL deferida (status "Aguardando Registro no REGIN")',
            req: true,
          },
        ],
      },
      {
        key: 'viab_junta',
        label: 'Pedido de Viabilidade na junta',
        orgao: 'JUCERJA / REGIN',
        checklist: [{ key: 'viab_junta_ok', label: 'Viabilidade aprovada (REGIN)', req: true }],
      },
    ],
    protocoloLabels: {
      viabilidade: 'Pedido de Viabilidade (REGIN)',
      coletor: 'DBE',
      registro: 'Protocolo Web',
    },
    // RJ NÃO usa DUDA (isso é DETRAN). Emolumentos = tabela FLAT por TIPO SOCIETÁRIO
    // (não "por ato/porte") — mesmo valor p/ constituição/alteração/transformação;
    // Anexo I da Deliberação JUCERJA nº 171/2025 (vigência 02/01/2026). O Anexo II é
    // de serviços do DREI (empresa estrangeira etc.), recolhidos por DARF 6621 — não
    // é fee de alteração comum. Valores mudam por deliberação anual → NÃO hardcodar.
    guia: {
      nome: 'Emolumentos JUCERJA',
      tabelaUrl: 'https://www.jucerja.rj.gov.br/Informacoes/TabelaPrecos',
      // Isenção de EXTINÇÃO/baixa (art. 55 §2º Lei 8.934/1994) — é LEI FEDERAL, vale
      // p/ todas as juntas e pertence ao fluxo de BAIXA, não ao de alteração. V-RJ-03.
      distratoIsento: true,
    },
    prazoInatividadeDias: 30, // FIT — cancela por inatividade
    prazoNota:
      'Abrir a FIT (Ficha de Informação Técnica) em até 30 dias corridos, senão o protocolo é cancelado por inatividade (JUCERJA).',
    portalUrl: 'https://www.jucerja.rj.gov.br/Servicos/Regin',
    orgaosPosRegistro: [
      { nome: 'SEFAZ-RJ', url: 'https://portal.fazenda.rj.gov.br/cadastro/' },
      { nome: 'Prefeitura (Carioca Digital)' },
    ],
    extras: { protecaoCpf: true },
  },

  // ── RJ-interior · REGIN + prefeitura local, SEM Carioca-Digital (RASCUNHO) ──
  // A CPL é feita no Carioca Digital = portal da PREFEITURA DA CIDADE DO RIO
  // (IPLANRIO). Municípios do interior (Niterói, Duque de Caxias…) têm sistemas
  // próprios de licenciamento integrados ao REGIN → cair no fallback capital
  // injetaria um passo (CPL Carioca-Digital) inexistente. V-RJ-04 (2026-07-09).
  // Conteúdo fino do interior ainda a validar (HIP) — aqui o objetivo é só matar
  // o falso-positivo da CPL. `resolverRegistry` já casa por `municipioScope`.
  {
    id: 'RJ-interior',
    uf: 'RJ',
    municipioScope: 'interior',
    nome: 'JUCERJA · interior (rascunho)',
    portal: 'REGIN-RJ / Protocolo Web',
    slaViabilidadeDias: 2,
    viabilidadeSubpassos: [
      {
        key: 'viab_junta',
        label: 'Pedido de Viabilidade na junta',
        orgao: 'JUCERJA / REGIN + Prefeitura local',
        checklist: [{ key: 'viab_junta_ok', label: 'Viabilidade aprovada (REGIN)', req: true }],
      },
    ],
    protocoloLabels: {
      viabilidade: 'Pedido de Viabilidade (REGIN)',
      coletor: 'DBE',
      registro: 'Protocolo Web',
    },
    guia: {
      nome: 'Emolumentos JUCERJA',
      tabelaUrl: 'https://www.jucerja.rj.gov.br/Informacoes/TabelaPrecos',
      distratoIsento: true,
    },
    prazoInatividadeDias: 30,
    prazoNota:
      'Abrir a FIT (Ficha de Informação Técnica) em até 30 dias corridos, senão o protocolo é cancelado por inatividade (JUCERJA).',
    portalUrl: 'https://www.jucerja.rj.gov.br/Servicos/Regin',
    orgaosPosRegistro: [
      { nome: 'SEFAZ-RJ', url: 'https://portal.fazenda.rj.gov.br/cadastro/' },
      // CPL do Carioca Digital e SO da cidade do Rio; interior tem sistema
      // municipal proprio (a mapear) — por isso sem URL.
      { nome: 'Prefeitura local' },
    ],
    extras: { protecaoCpf: true },
  },

  // ══════════════════════════════════════════════════════════════════════════
  // UFs levantadas em 2026-08-17 (pesquisa externa com fonte oficial por item).
  // Ordem: MG, DF, BA-capital, BA-interior. Cobertura acumulada ≈ 95,5% da carteira.
  // ══════════════════════════════════════════════════════════════════════════

  // ── MG · JUCEMG, integrador PRÓPRIO (não REGIN, não VRE) ───────────────────
  // A UF com processo REAL em voo quando isto foi escrito (alteração em BH), e o
  // mais simples dos três: viabilidade em ato único, no padrão nacional.
  {
    id: 'MG',
    uf: 'MG',
    municipioScope: 'todos',
    nome: 'JUCEMG',
    portal: 'Portal de Serviços JUCEMG (Módulo Integrador)',
    viabilidadeSubpassos: [
      {
        key: 'viab_unica',
        label: 'Viabilidade',
        orgao: 'JUCEMG (nome) + Prefeitura (endereço)',
        checklist: [
          { key: 'nome_aprovado', label: 'Nome aprovado (junta comercial)', req: true },
          { key: 'endereco_aprovado', label: 'Endereço aprovado (prefeitura)', req: true },
        ],
      },
    ],
    protocoloLabels: {
      viabilidade: 'Consulta de Viabilidade',
      coletor: 'DBE',
      registro: 'Protocolo JUCEMG',
    },
    // DAE — Documento de Arrecadação Estadual da JUCEMG. Tabela em UFEMG desde 01/01/2025
    // (muda na virada do ano) → NÃO hardcodar valor.
    guia: { nome: 'DAE', tabelaUrl: 'https://jucemg.mg.gov.br/pagina/52/tabela-de-precos' },
    // Sem norma estadual de auto-cancelamento confirmada — só a regra federal de 30 dias
    // para cumprir exigência (IN DREI 81/2020, art. 53), que não é cancelamento de
    // protocolo parado. Por isso `prazoInatividadeDias` fica AUSENTE: preencher com 30
    // acenderia um alarme que não existe.
    portalUrl: 'https://portalservicos.jucemg.mg.gov.br/Portal/',
    orgaosPosRegistro: [
      // Sem URL: a pesquisa confirmou o ORGAO (SEF/MG), nao a pagina do servico. Link
      // inventado manda o operador pro lugar errado com a autoridade da tela.
      { nome: 'SEF/MG' },
      // Integração municipal PARCIAL (~403 municípios). O sistema varia por município —
      // sem URL, pra não mandar o operador pro lugar errado.
      { nome: 'Prefeitura' },
    ],
  },

  // ── DF · JUCIS-DF, LOCALIZAÇÃO ELIMINATÓRIA ANTES DO NOME ──────────────────
  // ⚠️ NÃO é ato único. A viabilidade tem duas fases no mesmo fluxo e a de LOCALIZAÇÃO
  // vem primeiro e é eliminatória — mesma estrutura da CPL do Rio. Configurar o DF pelo
  // padrão nacional esconderia um passo obrigatório.
  // E o DF não tem prefeitura: quem analisa endereço são as 35 ADMINISTRAÇÕES REGIONAIS
  // do GDF, à luz da LUOS (LC 948/2019) e do PDOT.
  {
    id: 'DF',
    uf: 'DF',
    municipioScope: 'todos', // DF não tem municípios — só regiões administrativas
    nome: 'JUCIS-DF',
    portal: 'RLE@Digital / Rede Simples DF',
    viabilidadeSubpassos: [
      {
        key: 'viab_localizacao',
        label: 'Viabilidade de Localização',
        orgao: 'Administração Regional (GDF)',
        checklist: [
          {
            key: 'endereco_aprovado',
            label: 'Localização aprovada (Administração Regional)',
            req: true,
          },
        ],
      },
      {
        key: 'viab_nome',
        label: 'Viabilidade de Nome Empresarial',
        orgao: 'JUCIS-DF',
        checklist: [{ key: 'nome_aprovado', label: 'Nome aprovado (JUCIS-DF)', req: true }],
      },
    ],
    protocoloLabels: {
      viabilidade: 'Consulta de Viabilidade (RLE)',
      coletor: 'DBE',
      registro: 'Protocolo JUCIS-DF',
    },
    // DAR — Documento de Arrecadação da JUCIS-DF. Tabela reajustada anualmente por
    // resolução (a de 2026 veio da Resolução nº 01 de 30/12/2025, DODF 247).
    guia: { nome: 'DAR', tabelaUrl: 'https://jucis.df.gov.br/tabela-de-precos/' },
    // Auto-cancelamento com dia-contagem próprio NÃO CONFIRMADO em norma da JUCIS-DF.
    portalUrl: 'https://www.redesimples.df.gov.br/',
    orgaosPosRegistro: [
      { nome: 'SEFAZ-DF' }, // URL do servico nao confirmada na pesquisa
      // Papel "municipal" (uso do solo, licenciamento) é das Administrações Regionais.
      // A pesquisa confirma que o papel municipal e das 35 Administracoes Regionais; a URL
      // que ela traz e da SDE, que e a secretaria que coordena o RLE — nao a AR. Rotulo
      // segue o link pra nao prometer uma pagina que nao existe.
      { nome: 'Registro e Licenciamento de Empresas (GDF)', url: 'https://www.sde.df.gov.br/' },
    ],
  },

  // ── BA-capital · JUCEB/REGIN, TVL da SUCOM ANTES da viabilidade ────────────
  // ⚠️ Regra de CAPITAL, não do estado. Verbatim JUCEB: "No caso da cidade de Salvador,
  // para dar seguimento ao pedido de viabilidade, é necessário já ter solicitado o Termo
  // de Viabilidade de Localização (TVL) municipal." Aplicar isso ao interior injetaria um
  // passo inexistente — é o erro que já aconteceu 3x em SP/RJ.
  {
    id: 'BA-capital',
    uf: 'BA',
    municipioScope: 'capital',
    nome: 'JUCEB · Salvador',
    portal: 'REGIN-BA / Requerimento Universal',
    viabilidadeSubpassos: [
      {
        key: 'tvl',
        label: 'TVL — Termo de Viabilidade de Localização',
        orgao: 'SUCOM (Prefeitura de Salvador)',
        // ⚠️ SEM `requerAlteracaoDe`, de propósito. Cheguei a gatear por analogia com a CPL
        // do Rio, mas o verbatim da JUCEB não faz esse recorte: diz que o TVL é necessário
        // "para dar seguimento ao pedido de viabilidade", sem qualificar o motivo. A direção
        // de falha do módulo é FAIL-OPEN (mostrar na dúvida) — esconder passo obrigatório é
        // o erro caro; mostrar um a mais custa um checkbox. Gatear exige confirmação.
        checklist: [{ key: 'tvl_solicitado', label: 'TVL solicitado (SUCOM)', req: true }],
      },
      {
        key: 'viab_junta',
        label: 'Pedido de Viabilidade na junta',
        orgao: 'JUCEB / REGIN',
        checklist: [{ key: 'nome_aprovado', label: 'Viabilidade aprovada (REGIN)', req: true }],
      },
    ],
    protocoloLabels: {
      viabilidade: 'Pedido de Viabilidade (REGIN)',
      coletor: 'DBE',
      registro: 'Protocolo JUCEB',
    },
    // DAM — Documento de Arrecadação Mercantil. NÃO é DAE. Tabelas separadas p/ capital
    // e interior; valores mudam por resolução → não hardcodar.
    guia: { nome: 'DAM', tabelaUrl: 'https://www.ba.gov.br/juceb/tabelas-de-precos-capital' },
    // Cancelamento por edital de protocolos em exigência há +60 dias. O NÚMERO da
    // resolução é ambíguo na própria fonte oficial (05/2022 vs 005/2023) — o prazo está
    // confirmado, a citação normativa não.
    prazoInatividadeDias: 60,
    prazoNota:
      'Protocolo em exigência há mais de 60 dias pode ser cancelado por edital (JUCEB). Base normativa com número ambíguo na fonte oficial — confirmar antes de citar.',
    portalUrl: 'https://www.ba.gov.br/juceb/redesim/sistema-integrador',
    orgaosPosRegistro: [
      { nome: 'SEFAZ-BA' }, // URL do servico nao confirmada na pesquisa
      { nome: 'Prefeitura de Salvador (SUCOM)' },
    ],
  },

  // ── BA-interior · JUCEB/REGIN, ato único (sem TVL) ─────────────────────────
  {
    id: 'BA-interior',
    uf: 'BA',
    municipioScope: 'interior',
    nome: 'JUCEB · interior',
    portal: 'REGIN-BA / Requerimento Universal',
    viabilidadeSubpassos: [
      {
        key: 'viab_unica',
        label: 'Viabilidade',
        orgao: 'JUCEB (nome) + Prefeitura (endereço)',
        checklist: [
          { key: 'nome_aprovado', label: 'Nome aprovado (junta comercial)', req: true },
          { key: 'endereco_aprovado', label: 'Endereço aprovado (prefeitura)', req: true },
        ],
      },
    ],
    protocoloLabels: {
      viabilidade: 'Pedido de Viabilidade (REGIN)',
      coletor: 'DBE',
      registro: 'Protocolo JUCEB',
    },
    guia: { nome: 'DAM', tabelaUrl: 'http://www.juceb.ba.gov.br/home/precos-e-prazos/' },
    prazoInatividadeDias: 60,
    prazoNota:
      'Protocolo em exigência há mais de 60 dias pode ser cancelado por edital (JUCEB). Base normativa com número ambíguo na fonte oficial — confirmar antes de citar.',
    portalUrl: 'https://www.ba.gov.br/juceb/redesim/sistema-integrador',
    orgaosPosRegistro: [
      { nome: 'SEFAZ-BA' }, // URL do servico nao confirmada na pesquisa
      // Integração municipal parcial — só municípios conveniados. Sistema varia.
      { nome: 'Prefeitura' },
    ],
  },
];

const CAPITAIS: Record<string, string> = {
  GO: 'Goiânia',
  SP: 'São Paulo',
  RJ: 'Rio de Janeiro',
  // BA entra aqui porque o TVL da SUCOM é regra de Salvador. Sem esta linha o registry
  // `BA-capital` nunca seria escolhido e o TVL sumiria da tela.
  BA: 'Salvador',
};

/**
 * Nome da guia de emolumentos por UF, para as UFs SEM registry próprio.
 *
 * Confirmado por fonte oficial em 2026-08-17. Entra no genérico porque é barato e tira o
 * "DARE" goiano da tela desses estados — guia com nome errado é o item que o operador
 * leva pro banco.
 *
 * ⚠️ PR ficou de fora de propósito: a junta só documenta "Guia" e "Boleto" como formas de
 * pagamento, e o nome oficial do instrumento não foi confirmado. Preencher com chute aqui
 * seria pior que o rótulo neutro.
 */
const GUIA_POR_UF: Record<string, string> = {
  CE: 'DAE',
  MS: 'GR Online',
  PE: 'DAE',
  SC: 'DARE',
};

/**
 * Prazo de auto-cancelamento de protocolo parado, por UF, para as UFs sem registry.
 *
 * ⚠️ SÓ entram UFs com norma estadual CONFIRMADA. Os 30 dias da IN DREI 81/2020 são para
 * CUMPRIR EXIGÊNCIA, não para cancelar protocolo parado — preencher com 30 acenderia no
 * card um alarme de perda de protocolo que não existe. Ausência aqui significa "sem norma
 * estadual confirmada", não "sem prazo nenhum".
 */
const PRAZO_INATIVIDADE_POR_UF: Record<string, { dias: number; nota: string }> = {
  SC: {
    dias: 60,
    nota: 'O requerimento é cancelado 60 dias após o despacho em exigência (Resolução Plenária JUCESC nº 1/2025).',
  },
  PR: {
    dias: 90,
    nota: 'Processo em exigência sem movimentação por 90 dias é cancelado; aos 60 dias a Jucepar publica relação dando 30 dias para reentrada (Resolução Plenária JUCEPAR 01/2023).',
  },
};

/**
 * Registry montado pelo PADRÃO NACIONAL, para UF sem levantamento próprio.
 *
 * O denominador comum foi verificado em 8 estados (pesquisa 2026-08-17), não presumido:
 * a viabilidade é ato único, a análise de endereço é sempre municipal, a exigência tem 30
 * dias corridos (IN DREI 81/2020) e o coletor é o DBE federal. É o mesmo desenho que GO,
 * MG e BA-interior têm depois de levantados — o que dá alguma confiança de que não é
 * ficção.
 *
 * O que NÃO tenta adivinhar, e por quê:
 *  - `portalUrl` ausente → o card do portal não aparece (melhor que link errado, que manda
 *    o operador pro lugar errado com a autoridade da tela);
 *  - `prazoInatividadeDias` só quando há norma estadual confirmada (ver o mapa acima);
 *  - `guia.nome` só quando confirmado; senão rótulo neutro em vez de sigla de outro estado;
 *  - `orgaosPosRegistro` sem URL — o sistema municipal varia e o órgão estadual não foi
 *    levantado nessas UFs.
 */
export function registryGenerico(uf: string): RegistryConfig {
  const u = uf.toUpperCase();
  const junta = JUNTA_POR_UF[u] ?? 'Junta comercial';
  const prazo = PRAZO_INATIVIDADE_POR_UF[u];
  return {
    id: `${u}-generico`,
    uf: u,
    municipioScope: 'todos',
    generico: true,
    nome: junta,
    portal: 'Integrador estadual (REDESIM)',
    viabilidadeSubpassos: [
      {
        key: 'viab_unica',
        label: 'Viabilidade',
        orgao: `${junta} (nome) + Prefeitura (endereço)`,
        checklist: [
          { key: 'nome_aprovado', label: 'Nome aprovado (junta comercial)', req: true },
          { key: 'endereco_aprovado', label: 'Endereço aprovado (prefeitura)', req: true },
        ],
      },
    ],
    protocoloLabels: {
      viabilidade: 'Protocolo de viabilidade',
      coletor: 'DBE',
      registro: 'Protocolo de registro',
    },
    // String vazia = sem nome confirmado nesta UF. Os dois consumidores (rótulo de campo
    // e item de checklist) suprimem o sufixo nesse caso, em vez de imprimir um nome
    // redundante ou o de outro estado.
    guia: { nome: GUIA_POR_UF[u] ?? '' },
    ...(prazo ? { prazoInatividadeDias: prazo.dias, prazoNota: prazo.nota } : {}),
    orgaosPosRegistro: [{ nome: 'SEFAZ estadual' }, { nome: 'Prefeitura' }],
  };
}

function normalizar(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
}

/**
 * A UF distingue capital × interior? (hoje SP, RJ e BA)
 *
 * ⚠️ Existe porque nessas UFs o município NÃO é detalhe cadastral: é o que decide se um
 * passo OBRIGATÓRIO aparece — CPL no Rio, TVL da SUCOM em Salvador. E `resolverRegistry`
 * resolve `ehCapital` por igualdade de texto, então município em branco cai calado no
 * INTERIOR, que é exatamente o registry SEM o passo. O modal de criação usa isto para
 * exigir o município nessas UFs, matando a ambiguidade na origem em vez de adivinhar
 * depois.
 */
export function ufExigeMunicipio(uf?: string | null): boolean {
  if (!uf) return false;
  return REGISTRIES.filter(r => r.uf === uf.toUpperCase()).length > 1;
}

/**
 * Resolve o registry a partir de (UF, município). UF com 1 registry (ex.: GO) ignora o
 * município; UF com capital×interior (SP/RJ/BA) usa o município p/ decidir.
 *
 * ⚠️ UF SEM registry levantado NÃO devolve mais `undefined` — devolve o genérico
 * (`generico: true`). Antes o `undefined` caía no checklist estático do playbook, e o
 * efeito não era buraco, era SUBSTITUIÇÃO SILENCIOSA: a etapa "Demais órgãos" trocava
 * itens de rastreio (`req: false`) por itens obrigatórios (`req: true`), então a UF menos
 * configurada ficava com o gate MAIS rígido, e a nota da etapa afirmava adaptação por CNAE
 * que não acontecia. Com o genérico o comportamento fica uniforme nas 27 UFs, e a tela
 * passa a poder dizer que o fluxo não foi levantado ali (ver `generico`).
 *
 * `undefined` só sobra pra chamada sem UF — que é ausência de dado, não UF não suportada.
 */
export function resolverRegistry(
  uf?: string | null,
  municipio?: string | null,
): RegistryConfig | undefined {
  if (!uf) return undefined;
  const u = uf.toUpperCase();
  const daUf = REGISTRIES.filter(r => r.uf === u);
  if (daUf.length === 0) return registryGenerico(u);
  if (daUf.length === 1) return daUf[0];
  const ehCapital = !!municipio && normalizar(municipio) === normalizar(CAPITAIS[u] ?? '');
  const escopo: MunicipioScope = ehCapital ? 'capital' : 'interior';
  return (
    daUf.find(r => r.municipioScope === escopo) ??
    daUf.find(r => r.municipioScope === 'todos') ??
    daUf[0]
  );
}
